import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { EstablishmentProvider } from "./context/EstablishmentContext";
import { QueryProvider } from "./lib/queryClient";
import { Toaster } from "./components/ui/sonner";
import { PWAInstallPrompt } from "./components/PWAInstallPrompt";
import { ScrollToTop } from "./components/ScrollToTop";
import { ChatbotWidget } from "./components/ChatbotWidget";
import { useLocation } from "react-router-dom";

// Pages
import LandingPage from "./pages/LandingPage";
import DevenirPartenaire from "./pages/DevenirPartenaire";
import RestaurantList from "./pages/RestaurantList";
import RestaurantDetail from "./pages/RestaurantDetail";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import GuestOrderConfirmation from "./pages/GuestOrderConfirmation";
import OrderDetail from "./pages/OrderDetail";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import RestaurantSignup from "./pages/RestaurantSignup";
import WizardPreview from "./pages/onboarding/WizardPreview";
import RestaurantOnboarding from "./pages/onboarding/RestaurantOnboarding";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import AuthCallback from "./pages/AuthCallback";
import ClientProfile from "./pages/ClientProfile";
import SearchDishes from "./pages/SearchDishes";
import Favorites from "./pages/Favorites";
import RestaurantDashboard from "./pages/restaurant/Dashboard";
import RestaurantMenu from "./pages/restaurant/Menu";
import RestaurantSettings from "./pages/restaurant/Settings";
import RestaurantOrders from "./pages/restaurant/Orders";
import RestaurantReviews from "./pages/restaurant/Reviews";
import RestaurantAnalytics from "./pages/restaurant/Analytics";
import RestaurantHours from "./pages/restaurant/HoursManagement";
import RestaurantChat from "./pages/restaurant/Chat";
import RestaurantGallery from "./pages/restaurant/Gallery";
import RestaurantDeposits from "./pages/restaurant/Deposits";
import PromotionsHub from "./pages/restaurant/PromotionsHub";
import MultiSiteManagement from "./pages/restaurant/MultiSiteManagement";
import ReferralDashboard from "./pages/restaurant/Referral";
import CommissionsDashboard from "./pages/restaurant/Commissions";
import StripeConnectPage from "./pages/restaurant/StripeConnect";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminRestaurants from "./pages/admin/Restaurants";
import AdminUsers from "./pages/admin/Users";
import AdminOrders from "./pages/admin/Orders";
import AdminSettings from "./pages/admin/Settings";
import AdminAnalytics from "./pages/admin/Analytics";
import AdminDisputes from "./pages/admin/Disputes";
import AdminReviews from "./pages/admin/Reviews";
import AdminHealth from "./pages/admin/Health";
import AdminChat from "./pages/admin/Chat";
import AdminOnboarding from "./pages/admin/Onboarding";
import AdminAuditLogs from "./pages/admin/AuditLogs";
import AdminWeeklyReports from "./pages/admin/WeeklyReports";
import AdminCommissions from "./pages/admin/Commissions";
import AdminLeads from "./pages/admin/Leads";
import AdminEmailPreview from "./pages/admin/EmailPreview";
import AdminEmailSettings from "./pages/admin/EmailSettings";
import CGU from "./pages/CGU";
import CGV from "./pages/CGV";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import Legal from "./pages/Legal";

// Smart Chatbot that passes restaurant context when on restaurant page
// DISABLED: To re-enable, change CHATBOT_ENABLED to true
const CHATBOT_ENABLED = false;

const SmartChatbot = () => {
  const location = useLocation();
  
  // Chatbot disabled - return early but after hooks
  if (!CHATBOT_ENABLED) return null;
  
  // Extract restaurant ID from URL if on restaurant detail page
  const restaurantMatch = location.pathname.match(/^\/restaurant\/([^/]+)$/);
  const restaurantId = restaurantMatch ? restaurantMatch[1] : null;
  
  // Don't show on dashboard pages (restaurant owners have their own chat)
  if (location.pathname.startsWith('/restaurant/') && !restaurantId) {
    return null;
  }
  
  // Don't show on admin pages
  if (location.pathname.startsWith('/admin')) {
    return null;
  }
  
  return <ChatbotWidget restaurantId={restaurantId} />;
};

// Protected Route Component
const ProtectedRoute = ({ children, roles = [] }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (roles.length > 0 && !roles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Public Route - redirect if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    if (user?.role === 'Restaurant') {
      return <Navigate to="/restaurant-dashboard" replace />;
    }
    if (user?.role === 'Admin') {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/restaurants" replace />;
  }
  
  return children;
};

function AppRoutes() {
  const location = useLocation();
  
  // Détection synchrone du session_id (OAuth callback)
  // IMPORTANT: Doit être AVANT les routes pour éviter les race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Auth Callback Route - doit être en premier */}
      <Route path="/auth/callback" element={<AuthCallback />} />
      
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/devenir-partenaire" element={<DevenirPartenaire />} />
      <Route path="/restaurants" element={<RestaurantList />} />
      <Route path="/search" element={<SearchDishes />} />
      <Route path="/restaurant/:id" element={<RestaurantDetail />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
      <Route path="/restaurant-signup" element={<PublicRoute><RestaurantOnboarding /></PublicRoute>} />
      <Route path="/restaurant-signup-old" element={<PublicRoute><RestaurantSignup /></PublicRoute>} />
      <Route path="/wizard-preview" element={<WizardPreview />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      
      {/* Legal Pages */}
      <Route path="/cgu" element={<CGU />} />
      <Route path="/cgv" element={<CGV />} />
      <Route path="/politique-de-confidentialite" element={<PrivacyPolicy />} />
      <Route path="/mentions-legales" element={<Legal />} />
      
      {/* Client Routes */}
      <Route path="/checkout" element={<Checkout />} />
      <Route path="/commande-confirmee" element={<GuestOrderConfirmation />} />
      <Route path="/order-confirmation" element={<ProtectedRoute roles={['Client']}><OrderConfirmation /></ProtectedRoute>} />
      <Route path="/commandes/:id" element={<ProtectedRoute roles={['Client', 'Restaurant', 'Admin']}><OrderDetail /></ProtectedRoute>} />
      <Route path="/mon-compte" element={<ProtectedRoute roles={['Client']}><ClientProfile /></ProtectedRoute>} />
      <Route path="/favoris" element={<ProtectedRoute roles={['Client']}><Favorites /></ProtectedRoute>} />
      
      {/* Restaurant Dashboard Routes */}
      <Route path="/restaurant-dashboard" element={<ProtectedRoute roles={['Restaurant']}><RestaurantDashboard /></ProtectedRoute>} />
      <Route path="/restaurant-dashboard/menu" element={<ProtectedRoute roles={['Restaurant']}><RestaurantMenu /></ProtectedRoute>} />
      <Route path="/restaurant-dashboard/orders" element={<ProtectedRoute roles={['Restaurant']}><RestaurantOrders /></ProtectedRoute>} />
      <Route path="/restaurant-dashboard/reviews" element={<ProtectedRoute roles={['Restaurant']}><RestaurantReviews /></ProtectedRoute>} />
      <Route path="/restaurant-dashboard/analytics" element={<ProtectedRoute roles={['Restaurant']}><RestaurantAnalytics /></ProtectedRoute>} />
      <Route path="/restaurant-dashboard/promo-codes" element={<ProtectedRoute roles={['Restaurant']}><PromotionsHub /></ProtectedRoute>} />
      <Route path="/restaurant-dashboard/hours" element={<ProtectedRoute roles={['Restaurant']}><RestaurantHours /></ProtectedRoute>} />
      <Route path="/restaurant-dashboard/settings" element={<ProtectedRoute roles={['Restaurant']}><RestaurantSettings /></ProtectedRoute>} />
      
      {/* Restaurant Dashboard Alias Routes (for /restaurant/* paths) */}
      <Route path="/restaurant/menu" element={<ProtectedRoute roles={['Restaurant']}><RestaurantMenu /></ProtectedRoute>} />
      <Route path="/restaurant/orders" element={<ProtectedRoute roles={['Restaurant']}><RestaurantOrders /></ProtectedRoute>} />
      <Route path="/restaurant/reviews" element={<ProtectedRoute roles={['Restaurant']}><RestaurantReviews /></ProtectedRoute>} />
      <Route path="/restaurant/analytics" element={<ProtectedRoute roles={['Restaurant']}><RestaurantAnalytics /></ProtectedRoute>} />
      <Route path="/restaurant/promo-codes" element={<ProtectedRoute roles={['Restaurant']}><PromotionsHub /></ProtectedRoute>} />
      <Route path="/restaurant/hours" element={<ProtectedRoute roles={['Restaurant']}><RestaurantHours /></ProtectedRoute>} />
      <Route path="/restaurant/settings" element={<ProtectedRoute roles={['Restaurant']}><RestaurantSettings /></ProtectedRoute>} />
      <Route path="/restaurant/chat" element={<ProtectedRoute roles={['Restaurant']}><RestaurantChat /></ProtectedRoute>} />
      <Route path="/restaurant/gallery" element={<ProtectedRoute roles={['Restaurant']}><RestaurantGallery /></ProtectedRoute>} />
      <Route path="/restaurant/deposits" element={<ProtectedRoute roles={['Restaurant']}><RestaurantDeposits /></ProtectedRoute>} />
      <Route path="/restaurant/promotions" element={<ProtectedRoute roles={['Restaurant']}><PromotionsHub /></ProtectedRoute>} />
      <Route path="/restaurant/multi-site" element={<ProtectedRoute roles={['Restaurant']}><MultiSiteManagement /></ProtectedRoute>} />
      <Route path="/restaurant/referral" element={<ProtectedRoute roles={['Restaurant']}><ReferralDashboard /></ProtectedRoute>} />
      <Route path="/restaurant/commissions" element={<ProtectedRoute roles={['Restaurant']}><CommissionsDashboard /></ProtectedRoute>} />
      <Route path="/restaurant/stripe-connect" element={<ProtectedRoute roles={['Restaurant']}><StripeConnectPage /></ProtectedRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute roles={['Admin']}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/restaurants" element={<ProtectedRoute roles={['Admin']}><AdminRestaurants /></ProtectedRoute>} />
      <Route path="/admin/users" element={<ProtectedRoute roles={['Admin']}><AdminUsers /></ProtectedRoute>} />
      <Route path="/admin/orders" element={<ProtectedRoute roles={['Admin']}><AdminOrders /></ProtectedRoute>} />
      <Route path="/admin/disputes" element={<ProtectedRoute roles={['Admin']}><AdminDisputes /></ProtectedRoute>} />
      <Route path="/admin/reviews" element={<ProtectedRoute roles={['Admin']}><AdminReviews /></ProtectedRoute>} />
      <Route path="/admin/health" element={<ProtectedRoute roles={['Admin']}><AdminHealth /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute roles={['Admin']}><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/settings" element={<ProtectedRoute roles={['Admin']}><AdminSettings /></ProtectedRoute>} />
      <Route path="/admin/chat" element={<ProtectedRoute roles={['Admin']}><AdminChat /></ProtectedRoute>} />
      <Route path="/admin/onboarding" element={<ProtectedRoute roles={['Admin']}><AdminOnboarding /></ProtectedRoute>} />
      <Route path="/admin/audit" element={<ProtectedRoute roles={['Admin']}><AdminAuditLogs /></ProtectedRoute>} />
      <Route path="/admin/weekly-reports" element={<ProtectedRoute roles={['Admin']}><AdminWeeklyReports /></ProtectedRoute>} />
      <Route path="/admin/commissions" element={<ProtectedRoute roles={['Admin']}><AdminCommissions /></ProtectedRoute>} />
      <Route path="/admin/leads" element={<ProtectedRoute roles={['Admin']}><AdminLeads /></ProtectedRoute>} />
      <Route path="/admin/emails" element={<ProtectedRoute roles={['Admin']}><AdminEmailPreview /></ProtectedRoute>} />
      <Route path="/admin/email-settings" element={<ProtectedRoute roles={['Admin']}><AdminEmailSettings /></ProtectedRoute>} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <QueryProvider>
          <AuthProvider>
            <EstablishmentProvider>
              <CartProvider>
                <ScrollToTop />
                <AppRoutes />
                <Toaster position="top-right" richColors />
                <PWAInstallPrompt />
                <SmartChatbot />
              </CartProvider>
            </EstablishmentProvider>
          </AuthProvider>
        </QueryProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
